import { Semantics } from "shared/components/drops/DropsBadge";
import { ColorTheme } from "shared/theme/theme";
import { capitalize } from "shared/utils/objectUtils";
import { DefaultTheme, useTheme } from "styled-components";

// The typing for the function below. Adds an alias to every semantic color
export type ThemeSemantic = ColorTheme & {
  [K in keyof ColorTheme as K extends `${infer Prefix}${Capitalize<Semantics>}${infer Suffix}`
    ? `${Prefix}Semantic${Suffix}`
    : K]: string;
};

/**
  Adds an alias to every semantic color (e.x. colorNeutralFill -> colorSemanticFill)
  For instance:
    switch (semantics) {
      case "alarm": return theme.colorAlarmFill
      case "secondaryAlarm": return theme.colorSecondaryAlarmFill
      case "warning": return theme.colorWarningFill
      case "info": return theme.colorInfoFill
      case "success": return theme.colorSuccessFill
      case "neutral": default: return theme.colorNeutralFill
    }
  Becomes:
    const theme = useThemeSemantic(semantics)
    return theme.colorSemanticFill
 */
export const useThemeSemantic = (semantics: Semantics) =>
  new Proxy(useTheme(), {
    get(target: DefaultTheme, property: keyof ThemeSemantic | symbol) {
      if (typeof property !== "string") {
        return Reflect.get(target, property);
      }
      return target[
        property.replace(
          "Semantic",
          capitalize(semantics),
        ) as keyof DefaultTheme
      ];
    },
  }) as ThemeSemantic;

/**
  If `semantics` is undefined then the default `useTheme` is returned instead.
  Thus, using`theme.colorSemantic` and such will give undefined instead, allowing you
  to add fallback colors.
 
  e.g:
  const semantic = "alarm"
  const theme = useThemeSemanticFallback(semantic)
  console.log(theme.colorSemantic ?? theme.colorOutlineSecondary) // theme.colorAlarm

  const semantic = undefined
  const theme = useThemeSemanticFallback(semantic)
  console.log(theme.colorSemantic ?? theme.colorOutlineSecondary) // theme.colorOutlineSecondary
 */
export const useThemeOrSemantic = (semantics: Semantics | undefined) => {
  const theme = useTheme();
  const themeSemantic = useThemeSemantic(semantics ?? "neutral");
  return semantics ? themeSemantic : (theme as ThemeSemantic);
};
